import React from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import {
  Panel,
  PanelEmpty,
  CHART_BASE,
  CHART_GRID_NO_X
} from '../../../../component/common/designSystem';

/**
 * One family of assessments as a 100% stacked bar per questionnaire — the
 * quartile spread for scored assessments, the style spread for attachment ones.
 *
 * Proportions rather than counts is the whole point. The most-taken assessment
 * always has the tallest bar, so a raw count chart just re-draws the popularity
 * ranking that the take-up table already shows. Normalising each row to its own
 * total is what makes a rarely-taken questionnaire comparable to a popular one,
 * and it is the only view in which "most people land in the bottom quartile of
 * this one" is visible at all.
 *
 * Rows must share an outcome vocabulary, which is why scored and attachment
 * assessments get separate panels: their labels are not the same axis and
 * stacking them together would be meaningless.
 */
const OutcomeMixPanel = ({ title, subtitle, rows, colors, emptyText, height }) => {
  // Apex draws horizontal categories bottom-up, so the most-taken assessment is
  // reversed into the top slot.
  const ordered = [...rows].reverse();
  const outcomeLabels = rows[0]?.outcomes?.map((outcome) => outcome.label) || [];

  const series = outcomeLabels.map((label, index) => ({
    name: label,
    data: ordered.map((row) => row.outcomes?.[index]?.count || 0)
  }));

  const options = {
    chart: { ...CHART_BASE, type: 'bar', stacked: true, stackType: '100%' },
    colors,
    plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '65%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID_NO_X,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: {
      categories: ordered.map((row) => row.label),
      labels: { formatter: (val) => `${Math.round(val)}%` }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val) => `${val.toLocaleString()} completions` }
    }
  };

  return (
    <Panel title={title} subtitle={subtitle}>
      {rows.length > 0 ? (
        <Chart type="bar" height={height} width="100%" series={series} options={options} />
      ) : (
        <PanelEmpty>{emptyText}</PanelEmpty>
      )}
    </Panel>
  );
};

OutcomeMixPanel.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  rows: PropTypes.array,
  colors: PropTypes.array,
  emptyText: PropTypes.string,
  height: PropTypes.number
};

OutcomeMixPanel.defaultProps = {
  rows: [],
  colors: [],
  emptyText: 'No completions recorded yet.',
  height: 340
};

export default OutcomeMixPanel;
